import { useAuth, useUser } from "@clerk/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { LoadingSpinner } from "@/components/global/elements/loading-spinner"
import { PageHeader } from "@/components/layout"
import { ManageExpansionForm } from "@/components/manage-content/manage-expansion-form"
import { useOrpc } from "@/integrations/orpc"
import { useProductionEntity } from "@/queries/production/use-production-entity"
import {
	detectExpansionFieldDifferences,
	transformProductionToExpansionFormData,
	transformStagingToExpansionFormData,
} from "@/utils/form.helpers"

export const Route = createFileRoute("/expansions/$id/manage")({
	component: ManageExpansionPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			gameSlug: search.gameSlug as string | undefined,
		}
	},
})

function ManageExpansionPage() {
	const { id } = Route.useParams()
	const searchParams = Route.useSearch()
	const { user } = useUser()
	const userEmail =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses?.[0]?.emailAddress ??
		"unknown@cardnexus.com"
	const { client } = useOrpc()
	const { isSignedIn } = useAuth()

	const gameSlug = searchParams.gameSlug || ""
	const expansionSlug = id

	const {
		data: stagingData,
		isLoading: stagingLoading,
		error: stagingError,
	} = useQuery({
		queryKey: [
			"gameAdmin",
			"staging",
			"getEntity",
			"expansion",
			gameSlug,
			expansionSlug,
		],
		queryFn: async () => {
			const result = await client.staging.getEntity({
				entityType: "expansion",
				gameSlug: gameSlug,
				entitySlug: expansionSlug,
			})

			if (result.entityType !== "expansion") {
				throw new Error(`Expected expansion entity, got ${result.entityType}`)
			}

			return result
		},
		enabled: isSignedIn && !!gameSlug && !!expansionSlug,
	})

	const { data: productionData, isLoading: productionLoading } =
		useProductionEntity({
			gameSlug,
			entitySlug: expansionSlug,
			entityType: "expansion",
			enabled: isSignedIn && !!gameSlug && !!expansionSlug,
		})

	const stagingFormData = transformStagingToExpansionFormData(stagingData)
	const productionFormData =
		transformProductionToExpansionFormData(productionData)

	const fieldDifferences = detectExpansionFieldDifferences(
		productionFormData,
		stagingFormData,
	)

	const isLoading = stagingLoading || productionLoading

	if (isLoading) {
		return (
			<div className="flex justify-center p-6">
				<LoadingSpinner />
			</div>
		)
	}

	if (stagingError || !stagingFormData) {
		return (
			<div className="p-6">
				<PageHeader
					title="Expansion not found"
					description={
						stagingError
							? stagingError.message
							: `The expansion with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	return (
		<ManageExpansionForm
			productionData={productionFormData}
			stagingData={stagingFormData}
			fieldDifferences={fieldDifferences}
			gameSlug={gameSlug}
			expansionSlug={expansionSlug}
			userEmail={userEmail}
		/>
	)
}
